/**
 * The shareable, plain-text rendering of an accessibility report — the sales artifact behind
 * "upload your form, see exactly where it stands". It is deliberately plain so it copies cleanly
 * into an email or a ticket and prints without styling.
 *
 * HONESTY IS THE CONTRACT. This text states what the file HAS and LACKS; it claims no conformance,
 * names no standard as MET, and marks every heuristic finding as advisory. It is the same posture
 * the card renders, flattened to text — nothing is asserted here that the card would hedge.
 */
import { AccessibilityReport, AccessibilityFinding } from '../types/accessibilityReport';
import { recommendationFor, REMEDIATION_REFERENCE_PATH } from './remediationRecommendations';

const TIER_TITLES: Record<string, string> = {
  DOCUMENT: 'DOCUMENT',
  CONTENT: 'CONTENT',
  INTERACTIVE: 'INTERACTIVE',
};

/** Sum of heading elements (H, H1..H6) in the structure histogram. */
export function headingCount(histogram?: Record<string, number>): number {
  if (!histogram) {
    return 0;
  }
  // ?? not || throughout: a key present with 0 is a real count, and || 0 would mask nothing here
  // but the pattern is kept consistent with the rest of the feature where 0 is meaningful.
  let total = histogram['H'] ?? 0;
  for (let level = 1; level <= 6; level++) {
    total += histogram[`H${level}`] ?? 0;
  }
  return total;
}

function documentHasLines(report: AccessibilityReport): string[] {
  const d = report.document;
  if (!d) {
    return [];
  }
  return [
    `  - Declared as tagged (Marked): ${d.marked ? 'yes' : 'no'}`,
    `  - Structure (tags) tree present: ${d.hasStructureTree ? 'yes' : 'no'}`,
    `  - Language: ${d.language ? d.language : 'not set'}`,
    `  - Document title announced: ${d.hasDcTitle && d.displayDocTitle ? 'yes' : 'no'}`,
    `  - PDF/UA conformance claim: ${d.hasPdfUaIdentifier ? 'present' : 'none (often correct)'}`,
    `  - Pages: ${d.pageCount ?? 0}`,
  ];
}

function contentHasLines(report: AccessibilityReport): string[] {
  const c = report.content;
  if (!c) {
    return [];
  }
  return [
    `  - Tagged structure elements: ${c.totalElements ?? 0}`,
    `  - Headings tagged (H1..Hn): ${headingCount(c.histogram)}`,
    `  - Paragraphs tagged (P): ${c.histogram?.['P'] ?? 0}`,
    `  - Figures tagged: ${c.histogram?.['Figure'] ?? 0}`,
    `  - Images drawn on pages: ${c.imageCount ?? 0}`,
    `  - Body text characters: ${c.textCharacterCount ?? 0}`,
  ];
}

function interactiveHasLines(report: AccessibilityReport): string[] {
  const i = report.interactive;
  if (!i) {
    return [];
  }
  const named = (i.fieldCount ?? 0) - (i.fieldsMissingTu ?? 0);
  const described = (i.linkCount ?? 0) - (i.linksMissingContents ?? 0);
  return [
    `  - Form fields: ${i.fieldCount ?? 0} (with an accessible name: ${named})`,
    `  - Links: ${i.linkCount ?? 0} (with a description: ${described})`,
  ];
}

function findingLines(findings: AccessibilityFinding[]): string[] {
  if (findings.length === 0) {
    return ['  - Nothing flagged in this tier.'];
  }
  const lines: string[] = [];
  for (const f of findings) {
    const mark = f.advisory ? '[ADVISORY] ' : '';
    lines.push(`  - ${mark}${f.statement}`);
    lines.push(`      Fix (${f.key}): ${recommendationFor(f.key).summary}`);
  }
  return lines;
}

/**
 * Build the whole plain-text report. `filename` is the author's own file name, for the heading.
 */
export function buildShareableText(report: AccessibilityReport, filename?: string): string {
  const out: string[] = [];
  out.push('ACCESSIBILITY REPORT');
  if (filename) {
    out.push(`File: ${filename}`);
  }
  out.push('');
  out.push(
    'EXPERIMENTAL FEATURE: these findings have not yet been verified against an external'
  );
  out.push(
    'PDF/UA validator (veraPDF). Treat them as indicative, not authoritative, and please'
  );
  out.push(
    'verify these results against your own independent findings.'
  );
  out.push('');
  out.push(
    'This report describes the uploaded file as it stands. It is a diagnosis, not a certificate:'
  );
  out.push(
    'it does not assert conformance with any standard, and findings marked [ADVISORY] are'
  );
  out.push('heuristic hints ("looks like a heading"), not established facts.');
  out.push('');

  const tiers: Array<AccessibilityFinding['tier']> = ['DOCUMENT', 'CONTENT', 'INTERACTIVE'];
  const hasBuilders: Record<string, (r: AccessibilityReport) => string[]> = {
    DOCUMENT: documentHasLines,
    CONTENT: contentHasLines,
    INTERACTIVE: interactiveHasLines,
  };

  for (const tier of tiers) {
    out.push(`== ${TIER_TITLES[tier]} ==`);
    out.push('What this document has:');
    const has = hasBuilders[tier](report);
    out.push(...(has.length > 0 ? has : ['  - (not analysed)']));
    out.push('What it lacks:');
    out.push(...findingLines(report.findings.filter(f => f.tier === tier)));
    out.push('');
  }

  out.push(`Authoritative fixes: ${REMEDIATION_REFERENCE_PATH} (look up each key above).`);
  return out.join('\n');
}
